import { api } from './client'

// Danh mục bán + nhóm sản phẩm bán lẻ, dùng chung website + Zalo Mini App
// (hợp đồng v3 §14). Ảnh là path tương đối /uploads/... của ERP.

export interface ShopCategory {
    id: string
    name: string
    slug: string
    image_url: string | null
    description: string | null
    sort_order: number
    show_web: boolean
    show_miniapp: boolean
    seo_title: string | null
    seo_description: string | null
    group_count: number
}

export interface ShopCategoryInput {
    name: string
    slug: string
    image_url: string | null
    description: string | null
    show_web: boolean
    show_miniapp: boolean
    seo_title: string | null
    seo_description: string | null
}

export interface CategoryRef {
    id: string
    name: string
    slug: string
}

export interface ShopGroupRow {
    id: string
    name: string
    slug: string
    cover_image_url: string | null
    cover_thumb_url: string | null
    categories: CategoryRef[]
    variant_count: number
    in_stock_variants: number
    show_web: boolean
    show_miniapp: boolean
    best_seller_rank: number | null
    sort_order: number
}

export interface ShopGroupFilter {
    q?: string
    category_id?: string
    show_web?: boolean
    show_miniapp?: boolean
    in_stock?: boolean
    best_seller?: boolean
}

export interface ShopOCOP {
    visible: boolean
    stars: number | null
    level: string | null
    year: number | null
    decision: string | null
    valid_until: string | null
    certificate_title: string | null
    certificate_image_url: string | null
}

export interface ShopBrew {
    tea_amount: string | null
    water_amount: string | null
    water_temp: string | null
    steep_time: string | null
    infusions: string | null
    note: string | null
}

export interface ShopAdminVariant {
    product_id: string
    sku: string
    product_name: string
    unit: string
    label: string
    show_web: boolean
    show_miniapp: boolean
    in_stock: boolean
    stock_available: number
    price_incl_vat: number
    has_online_price: boolean
    sellable: boolean
    /** Ảnh chính sản phẩm ERP — ảnh của loại trên web / Mini App (chỉ đọc). */
    image_url: string | null
    thumb_url: string | null
}

export interface ShopAdminGroup {
    id: string
    name: string
    slug: string
    tagline: string | null
    highlights: string[]
    story_slug: string | null
    show_web: boolean
    show_miniapp: boolean
    best_seller_rank: number | null
    sort_order: number
    brew_html: string | null
    description_html: string | null
    seo_title: string | null
    seo_description: string | null
    seo_image_url: string | null
    edited_at: string | null
    updated_at: string
    ocop: ShopOCOP
    brew: ShopBrew
    category_ids: string[]
    variants: ShopAdminVariant[]
    related: CategoryRef[]
}

export interface ShopGroupInput {
    name: string
    slug: string
    category_ids: string[]
    tagline: string | null
    highlights: string[]
    variants: {
        product_id: string
        label: string
        show_web: boolean
        show_miniapp: boolean
        in_stock: boolean
    }[]
    brew: ShopBrew
    clear_brew_html: boolean
    clear_description_html: boolean
    /** Mô tả chi tiết (HTML từ trình soạn thảo); không gửi = giữ như cũ. */
    description_html?: string
    /** Hướng dẫn pha (HTML từ trình soạn thảo); không gửi = giữ, "" = xoá. */
    brew_html?: string
    related_ids: string[]
    story_slug: string | null
    ocop: ShopOCOP
    show_web: boolean
    show_miniapp: boolean
    best_seller_rank: number | null
    sort_order: number
    seo_title: string | null
    seo_description: string | null
    seo_image_url: string | null
}

export interface ShopSKUOption {
    product_id: string
    sku: string
    name: string
    unit: string
    is_active: boolean
    is_sellable: boolean
    stock_available: number
    group_id: string | null
    group_name: string | null
    price_incl_vat: number
}

export interface UploadedImage {
    url: string
    thumb_url: string
}

/** Đường dẫn ảnh thu nhỏ ERP tự sinh (§0.8 hợp đồng). */
export const thumbOf = (url: string) => `${url}.thumb.jpg`

export const shopCatalogApi = {
    categories: () => api.get<ShopCategory[]>('/shop-admin/categories'),
    createCategory: (input: ShopCategoryInput) =>
        api.post<ShopCategory>('/shop-admin/categories', input),
    updateCategory: (id: string, input: ShopCategoryInput) =>
        api.put<ShopCategory>(`/shop-admin/categories/${id}`, input),
    deleteCategory: (id: string) =>
        api.delete<null>(`/shop-admin/categories/${id}`),
    reorderCategories: (ids: string[]) =>
        api.put<ShopCategory[]>('/shop-admin/categories/order', { ids }),
    groups: (filter: ShopGroupFilter) =>
        api.get<ShopGroupRow[]>('/shop-admin/groups', { params: filter }),
    group: (id: string) => api.get<ShopAdminGroup>(`/shop-admin/groups/${id}`),
    createGroup: (input: ShopGroupInput) =>
        api.post<ShopAdminGroup>('/shop-admin/groups', input),
    updateGroup: (id: string, input: ShopGroupInput) =>
        api.put<ShopAdminGroup>(`/shop-admin/groups/${id}`, input),
    deleteGroup: (id: string) => api.delete<null>(`/shop-admin/groups/${id}`),
    setVisibility: (
        id: string,
        v: { show_web?: boolean; show_miniapp?: boolean },
    ) => api.patch<null>(`/shop-admin/groups/${id}/visibility`, v),
    skuPicker: (q: string) =>
        api.get<ShopSKUOption[]>('/shop-admin/sku-picker', { params: { q } }),
    uploadImage: (file: File) => {
        const form = new FormData()
        form.append('file', file)
        return api.post<UploadedImage>('/shop-admin/uploads', form)
    },
}
